import axios, { AxiosInstance } from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api`,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
}

api.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;

      if (
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.url?.includes('/sessions/')
      ) {
        return Promise.reject(error);
      }

    if (originalRequest.url?.includes('/sessions/refresh')) {
      window.dispatchEvent(new Event('auth:logout'));
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await api.post<{ token: string }>('/sessions/refresh');
      const newToken = data.token;

      api.defaults.headers.authorization = `Bearer ${newToken}`;
      originalRequest.headers.authorization = `Bearer ${newToken}`;

      window.dispatchEvent(
        new CustomEvent('auth:tokenRefreshed', { detail: newToken }),
      );

      processQueue(null, newToken);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      window.dispatchEvent(new Event('auth:logout'));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

const createApi = (apiVersion?: 'v2'): AxiosInstance =>
  axios.create({
    baseURL: `${process.env.REACT_APP_BASE_URL}/api/${
      apiVersion ? `${apiVersion}/` : ''
    }`,
    withCredentials: true,
  });

export default api;

export { createApi };
